import { useCallback, useRef, useState } from 'react';

import { checkMFARequirementStrict, verifyMFACode } from '@/lib/auth-utils';

import type { MFARequirement } from '@/lib/auth-utils';

export type RecoveryStepUpOutcome = 'ready' | 'check_failed' | 'code_required' | 'invalid_code';

/**
 * Raises a password-recovery session to aal2 before `updateUser`.
 *
 * A recovery link only yields an aal1 session, and Supabase Auth refuses a
 * password change at aal1 once the account has a verified TOTP factor
 * ("AAL2 session is required to update email or password when MFA is enabled").
 * An `indeterminate` lookup is retried at submit and blocks if it stays unknown.
 */
export function useRecoveryMfaStepUp() {
  const [requirement, setRequirement] = useState<MFARequirement | null>(null);
  // A check still in flight when the code verifies holds an aal1 snapshot; it
  // must not put the spent-code requirement back.
  const steppedUpRef = useRef(false);

  const check = useCallback(async (): Promise<MFARequirement> => {
    const next = await checkMFARequirementStrict();
    if (steppedUpRef.current) {
      return { status: 'not_required' };
    }
    setRequirement(next);
    return next;
  }, []);

  const stepUp = async (totpCode: string): Promise<RecoveryStepUpOutcome> => {
    const current =
      requirement === null || requirement.status === 'indeterminate' ? await check() : requirement;

    if (current.status === 'indeterminate') {
      return 'check_failed';
    }
    if (current.status === 'not_required') {
      return 'ready';
    }
    if (!/^\d{6}$/.test(totpCode)) {
      return 'code_required';
    }

    const { success } = await verifyMFACode(current.factorId, totpCode);
    if (!success) {
      return 'invalid_code';
    }

    // The session is aal2 now; a resubmit after a server-side password
    // rejection must not replay the already-spent code.
    steppedUpRef.current = true;
    setRequirement({ status: 'not_required' });
    return 'ready';
  };

  return { isCodeRequired: requirement?.status === 'required', check, stepUp };
}
